<template>
  <el-dialog
    class="select-product-variants"
    :model-value="visible"
    @close="close"
    width="980px"
    :show-close="false"
  >
    <div class="select-product-variants__header">
      <h3>      
        <el-button type="text" @click="close">
          <svg-icon name="close" />
        </el-button>

        <span>
          {{ is_update || is_from_tablet ? 'Edit' : 'Select' }} Product
        </span>
      </h3>
      <div v-if="is_update" class="actions">
        <el-button 
          v-if="is_from_tablet && product.status_string === 'draft'"
          type="text" 
          @click="deleteProduct"
          class="delete-btn"
        >
          <svg-icon name="remove" />
        </el-button>
        <el-button 
          v-else-if="!is_from_tablet"
          type="text" 
          @click="remove"
          class="delete-btn"
        >
          <svg-icon name="remove" />
        </el-button>
        <el-button 
          type="secondary" 
          @click="prev"
          :disabled="!canPrev"
        >
          <svg-icon name="arrow-prev" />
        </el-button>
        <el-button 
          type="secondary" 
          @click="next"
          :disabled="!canNext"
        >
          <svg-icon name="arrow-next" />
        </el-button>
      </div>

    </div>
    
    <div class="select-product-variants__body" v-loading="is_loading" :class="{ 'storefront-layout': add_type === 'storefront' }">
      <div class="product-info">
        <div class="product-details">
          <div class="brand-name">{{ product.brand?.name }}</div>
          <div class="product-name">{{ product.name }}</div>
        </div>

        <div class="product-image">
          <el-image :src="selectedImage" :alt="product.name" @error="handleImageError" />
        </div>
      </div>

      <div class="variant-selections-wrapper">
        <div class="variant-selections">
          <div
            class="variant-section"
            v-if="product.unique_color_list && product.unique_color_count > 1"
          >
            <div class="variant-header">
              <h4>Select a Color {{ product.colorLabel }}</h4>
              <span class="variant-count">{{ is_from_tablet ? selectedVariants.length: product.unique_color_count }}</span>
            </div>

            <div class="selected-info" v-if="selectedColorsText">
              <span>Selected: {{ selectedColorsText }} {{ product.unique_size_count === 1 && selectedSizesText ? ', ' + selectedSizesText : '' }}</span>
            </div>

            <div class="finish-group" v-if="shimmerColors.length > 0">
              <!-- <h5>Shimmer finish</h5> -->
              <div class="color-options">
                <div
                  v-for="(color, index) in availableColors"
                  :key="index"
                  class="color-option"
                  :class="[{ 'active': isColorSelected(color.name) }, {'is-added': isTag('color', color.name) && !is_from_tablet}, {'is-disabled': color.disabled}]"
                  @click="!color.disabled && selectColor(color)"
                >
                  <svg-icon name="disabled" v-if="color.disabled" />
                  <svg-icon name="checked-circle" v-if="isColorSelected(color.name)" />
                  <el-image :src="color.swatch_image" :alt="color.name" />
                </div>
              </div>
            </div>
          </div>

          <div
            class="variant-section"
            v-if="product.unique_size_list && product.unique_size_count && product.unique_size_count > 1"
          >
            <div class="variant-header">
              <h4>Select a Size</h4>
              <span class="variant-count">{{ product.unique_size_count }}</span>
            </div>

            <div class="selected-info" v-if="selectedSizesText">
              <span>Selected: {{ selectedSizesText }} {{ product.unique_color_count === 1 && selectedColorsText ? ', ' + selectedColorsText : '' }}</span>
            </div>

            <div class="size-options">
              <div
                v-for="(size, index) in availableSizes"
                :key="index"
                class="size-option"
                :class="[{ active: isSizeSelected(size.value) }, {'is-added': isTag('size', size.value)}, {'is-disabled': size.disabled}]"
                @click="!size.disabled && selectSize(size.value)"
              >
                {{ size.value }}
                <svg-icon name="checked-circle" v-if="isSizeSelected(size.value)" />
              </div>
            </div>
          </div>
        </div>

        <div class="selected-variants" v-if="shouldShowMultiSelectionUI && selectedVariants.length > 0">
          <div class="selected-variants-header">
            <h4>Selected Variants ({{ selectedVariants.length }})</h4>
          </div>
          <div class="selected-variants-list">
            <div 
              v-for="variant in selectedVariants" 
              :key="variant.id"
              class="selected-variant-item"
            >
              <div class="variant-info">
                <template v-if="variant.color">{{ variant.color }}</template> <template v-if="variant.size">{{ variant.size }}</template>
              </div>
              <el-button 
                type="text" 
                size="small" 
                @click="removeVariant(variant.id)"
                class="remove-btn"
              >
                <svg-icon name="close" />
              </el-button>
            </div>
          </div>
        </div>
      </div>

      <ProductEditRightPanel
        v-if="add_type === 'storefront'"
        class="product-edit-right-panel"
        :product="product"
        @update:product="handleProductUpdate"
        @delete:product="deleteProduct"
      />
    </div>
 
    <div class="select-product-variants__footer">
      <!-- Error messages display -->
      <div v-if="validation_errors.length > 0" class="validation-errors">
        <div v-for="error in validation_errors" :key="error" class="error-message">
          {{ error }}
        </div>
      </div>

      <footer class="btns">
        <el-button
          v-if="!is_from_tablet"
          type="primary"
          size="large"
          class="add-button"
          @click="handleAdd"
          :disabled="!canAdd"
        >
          {{ buttonText }}
        </el-button>
        <el-button 
          v-if="shouldShowMultiSelectionUI"
          type="primary" 
          size="large" 
          @click="addCurrentVariant"
          :disabled="!canAddCurrentSelection"
        >
          Add Variant
        </el-button>

        <el-button
          v-if="is_from_tablet"
          type="primary" 
          size="large"
          @click="handleUpdate"
          :disabled="!canSaveChanges"
          >Save all changes
        </el-button>
      </footer>
    </div>
  </el-dialog>
  <confirm-dialog ref="confirm_dialog_el" :data="confirm_data" />
  <confirm-dialog ref="confirm_date_el" :data="confirm_date" />
</template>

<script setup>
import { ref, computed, onMounted, watch, getCurrentInstance } from 'vue';
import { ElDialog, ElButton, ElImage } from 'element-plus';
import ProductEditRightPanel from '../storefront/ProductEditRightPanel.vue';
import ConfirmDialog from '../storefront/collectionDetail/ConfirmDialog.vue';
import { useNotification } from '@/components/ui/notification';
import moment from 'moment';

const app = getCurrentInstance();
const ajax = app.appContext.config.globalProperties.$ajax;
const event = app.appContext.config.globalProperties.EventBus;
const emit = defineEmits(['remove-product']);

const { openNotBottom } = useNotification();
const visible = ref(false);
const product = ref({});
const selectedColor = ref(null);
const selectedSize = ref(null);
const selectedImage = ref('');
const shimmerColors = ref([]);
const selectedVariants = ref([]);
const initialSelectedVariants = ref([]);
const initialProductState = ref({});
const confirm_dialog_el = ref(null);
const confirm_date_el = ref(null);
const confirm_data = ref({
  title: '',
  description: '',
  action: () => {}
});
const confirm_date = ref({
  title: '',
  description: '',
  action: () => {}
});
const is_loading = ref(false);
const is_tag_from_url = ref(false);
const validation_errors = ref([]);
const current_product_index = ref(-1);
// const is_show_color_select = ref(true);
// const is_show_size_select = ref(true);
const is_update = ref(false);
const is_edit = ref(false);
const is_from_tablet = ref(false);
const props = defineProps({
  is_edit_mode: {
    type: Boolean,
    default: false
  },
  add_type: {
    type: String,
    default: 'storefront' // 'storefront', 'collection', 'post'
  },
  // content_id: {
  //   type: Number,
  //   default: 0
  // },
  // merchant_account_id: {
  //   type: Number,
  //   default: 0
  // },
  tagged_products: {
    type: Array,
    default: () => []
  }
});


// Computed properties for filtering available options based on selected variants
const availableColors = computed(() => {
  if (!product.value.total_color_size_map) {
    return shimmerColors.value.map(color => ({
      ...color,
      disabled: true
    }));
  }

  // Get all colors that exist in total_color_size_map
  const allExistingColors = new Set();
  product.value.total_color_size_map.forEach(item => {
    if (item.color) {
      allExistingColors.add(item.color);
    }
  });

  if (!selectedSize.value) {
    return shimmerColors.value.map(color => ({
      ...color,
      disabled: !allExistingColors.has(color.name)
    }));
  }
  
  const validColors = new Set();
  product.value.total_color_size_map.forEach(item => {
    if (item.size === selectedSize.value) {
      validColors.add(item.color);
    }
  });
  
  return shimmerColors.value.map(color => ({
    ...color,
    disabled: !allExistingColors.has(color.name) || !validColors.has(color.name)
  }));
});

const availableSizes = computed(() => {
  if (!product.value.unique_size_list) {
    return [];
  }
  
  if (!product.value.total_color_size_map) {
    return product.value.unique_size_list.map(size => ({
      value: size,
      disabled: true
    }));
  }

  // Get all sizes that exist in total_color_size_map
  const allExistingSizes = new Set();
  product.value.total_color_size_map.forEach(item => {
    if (item.size) {
      allExistingSizes.add(item.size);
    }
  });

  if (!selectedColor.value) {
    return product.value.unique_size_list.map(size => ({
      value: size,
      disabled: !allExistingSizes.has(size)
    }));
  }
  
  const validSizes = new Set();
  product.value.total_color_size_map.forEach(item => {
    if (item.color === selectedColor.value) {
      validSizes.add(item.size);
    }
  });
  
  return product.value.unique_size_list.map(size => ({
    value: size,
    disabled: !allExistingSizes.has(size) || !validSizes.has(size)
  }));
});

// Check if user can add current selection to variants
const canAddCurrentSelection = computed(() => {
  if (!product.value) return false;

  // Check if this combination already exists in selectedVariants
  const currentVariant = {
    color: selectedColor.value,
    size: selectedSize.value
  };

  const isMultiVariant = product.value.total_color_size_map.some(item => 
    currentVariant.color === item.color && currentVariant.size === item.size
  );

  // console.log('canAddCurrentSelection', isMultiVariant);

  const isDuplicate = selectedVariants.value.some(variant => 
    variant.color === currentVariant.color && variant.size === currentVariant.size
  );
  
  // return !is_loading.value && colorRequirementMet && sizeRequirementMet && !isDuplicate;
  return !is_loading.value && isMultiVariant && !isDuplicate;
});

// Check if we should show multi-selection features (UI)
const shouldShowMultiSelectionUI = computed(() => {
  const colorCount = product.value.unique_color_count || 0;
  const sizeCount = product.value.unique_size_count || 0;
  return colorCount > 1 && sizeCount > 1;
});

// Check if we should use multi-selection logic (only when single variant type)
const shouldUseMultiSelection = computed(() => {
  // Only use multi-selection when there's either multiple colors OR multiple sizes, but not both
  const hasMultipleColors = (product.value.unique_color_count || 0) > 1;
  const hasMultipleSizes = (product.value.unique_size_count || 0) > 1;
  
  // Multi-selection only when one type has multiple options and the other doesn't
  return (hasMultipleColors && !hasMultipleSizes) || (!hasMultipleColors && hasMultipleSizes);
});

// Get selected colors text for display
const selectedColorsText = computed(() => {
  if (shouldUseMultiSelection.value) {
    const selectedColors = [...new Set(selectedVariants.value.map(v => v.color).filter(Boolean))];
    return selectedColors.length > 0 ? selectedColors.join(', ') : null;
  } else {
    return selectedColor.value;
  }
});

// Get selected sizes text for display
const selectedSizesText = computed(() => {
  if (shouldUseMultiSelection.value) {
    const selectedSizes = [...new Set(selectedVariants.value.map(v => v.size).filter(Boolean))];
    return selectedSizes.length > 0 ? selectedSizes.join(', ') : null;
  } else {
    return selectedSize.value;
  }
});

const isSingleMultiVariant = computed(() => {
  const colorCount = product.value.unique_color_count || 0;
  const sizeCount = product.value.unique_size_count || 0;
  return (colorCount === 1 && sizeCount > 1) || (sizeCount === 1 && colorCount > 1);
});

const buttonText = computed(() => {
  if (is_update.value) {
    return 'Update products';
  } else if (isSingleMultiVariant.value) {
    return `Select${(selectedVariants.value.length - 1) > 0 ? ' (' + (selectedVariants.value.length - 1) + ')' : ''}`;
  } else {
    return `Select${selectedVariants.value.length > 0 ? ' (' + selectedVariants.value.length + ')' : ''}`;
  }
});

// Check if user can proceed with selected variants
const canAdd = computed(() => {
  // Basic loading and variant selection checks
  const basicChecks = (() => {
    if (is_update.value) {
      return !is_loading.value;
    }

    // if(product.value.status_string !== 'draft'){
    //   return false;
    // }

    if (shouldUseMultiSelection.value) {
      if (isSingleMultiVariant.value) {
        return !is_loading.value && (selectedVariants.value.length - 1) > 0;
      } else {
        return !is_loading.value && selectedVariants.value.length > 0;
      }
    } else if (shouldShowMultiSelectionUI.value) {
      return !is_loading.value && selectedVariants.value.length > 0;
    } else {
      const colorCount = product.value.unique_color_count || 0;
      const sizeCount = product.value.unique_size_count || 0;
      const colorRequirementMet = colorCount > 0 ? !!selectedColor.value : true;
      const sizeRequirementMet = sizeCount > 0 ? !!selectedSize.value : true;
      return !is_loading.value && colorRequirementMet && sizeRequirementMet;
    }
  })();

  // console.log('canAdd', props.add_type, basicChecks);
  // Skip ProductEditRightPanel validation for storefront
  if (props.add_type !== 'storefront') {
    return basicChecks;
  }

  // Required fields validation for non-storefront types
  const hasRecipient = product.value.post_to.data?.length > 0;
  
  // Schedule validation
  const publishValidation = !product.value.scheduled_start_enabled || 
    (product.value.scheduled_start?.val && product.value.scheduled_start?.date);
  const disableValidation = !product.value.scheduled_end_enabled || 
    (product.value.scheduled_end?.val && product.value.scheduled_end?.date);
  
  // Check if there are any validation errors
  const noValidationErrors = validation_errors.value.length === 0;

  return basicChecks && hasRecipient && publishValidation && disableValidation && noValidationErrors;


  // const hasCollections = product.value.collections?.length > 0;

  // return basicChecks && hasRecipient && hasCollections;
});

// Check if next navigation is possible
const canNext = computed(() => {
  return props.tagged_products.length > 0 && 
         current_product_index.value >= 0 && 
         current_product_index.value < props.tagged_products.length - 1;
});

// Check if previous navigation is possible  
const canPrev = computed(() => {
  return props.tagged_products.length > 0 && 
         current_product_index.value > 0;
});

// Check if tablet mode has changes to enable save button
const canSaveChanges = computed(() => {
  if (!is_from_tablet.value || is_loading.value || selectedVariants.value.length === 0 || !canAdd.value) {
    return false;
  }
  
  // Compare current selectedVariants with initial state
  const current = selectedVariants.value;
  const initial = initialSelectedVariants.value;
  
  if (current.length !== initial.length) {
    return true;
  }
  
  // Check if variants have changed
  const variantsChanged = !current.every(currentVariant => 
    initial.some(initialVariant => 
      currentVariant.color === initialVariant.color && 
      currentVariant.size === initialVariant.size
    )
  );
  
  if (variantsChanged) {
    return true;
  }
  
  // Check if product properties have changed (from ProductEditRightPanel)
  const currentProduct = product.value;
  const initialProduct = initialProductState.value;

  // Check status changes
  if (currentProduct.status_string !== initialProduct.status_string) {
    return true;
  }
  
  // Check post_to (recipient) changes
  if (currentProduct.post_to.data.length !== initialProduct.post_to.data.length) {
    return true;
  }
  
  // Deep compare post_to data items
  const postToChanged = !currentProduct.post_to.data.every((currentItem, index) => {
    const initialItem = initialProduct.post_to.data[index];
    return currentItem.id === initialItem?.id && 
           currentItem.name === initialItem?.name &&
           currentItem.motom_user_id === initialItem?.motom_user_id;
  });
  
  if (postToChanged) {
    return true;
  }
  
  // Check collections changes
  if (currentProduct.curation_collections.length !== initialProduct.curation_collections.length) {
    return true;
  }
  
  // Deep compare collections items
  const collectionsChanged = !currentProduct.curation_collections.every((currentItem, index) => {
    const initialItem = initialProduct.curation_collections[index];
    return currentItem.id === initialItem?.id && currentItem.name === initialItem?.name;
  });
  
  if (collectionsChanged) {
    return true;
  }

  // console.log(currentProduct.permission, initialProduct.permission);
  
  // Check permission changes
  if (currentProduct.permission.length !== initialProduct.permission.length) {
    return true;
  }
  
  // Deep compare permission items
  const permissionChanged = !currentProduct.permission.every((currentItem, index) => {
    const initialItem = initialProduct.permission[index];
    return currentItem === initialItem;
  });
  
  if (permissionChanged) {
    return true;
  }
  
  // Check schedule start changes
  // console.log(currentProduct.scheduled_start_enabled, initialProduct.scheduled_start_enabled);
  if (!!currentProduct.scheduled_start_enabled !== !!initialProduct.scheduled_start_enabled) {
    return true;
  }

  if (currentProduct.scheduled_start.val !== initialProduct.scheduled_start.val) {
    return true;
  }
  
  // Check schedule end changes
  if (!!currentProduct.scheduled_end_enabled !== !!initialProduct.scheduled_end_enabled) {
    return true;
  }

  if (currentProduct.scheduled_end.val !== initialProduct.scheduled_end.val) {
    return true;
  }
  
  // Check notify creators changes
  if (!!currentProduct.notify_creators !== !!initialProduct.notify_creators) {
    return true;
  }

  if(currentProduct.is_sponsored_disclosure_enabled !== initialProduct.is_sponsored_disclosure_enabled) {
    return true;
  }

  if(currentProduct.sponsored_disclosure_name !== initialProduct.sponsored_disclosure_name) {
    return true;
  }
  
  // Check click_all changes
  // if (currentProduct.click_all !== initialProduct.click_all) {
  //   return true;
  // }
  
  return false;
});

// Validation function to check required fields
const validateRequiredFields = () => {
  // Skip validation for storefront
  if (props.add_type !== 'storefront') {
    validation_errors.value = [];
    return true;
  }

  const errors = [];
  
  // Check Recipient
  if (!product.value.post_to.data?.length) {
    errors.push('Recipient is required');
  }
  
  // Check scheduled_start_enabled and scheduled_start
  if (product.value.scheduled_start_enabled && (!product.value.scheduled_start?.val || !product.value.scheduled_start?.date)) {
    errors.push('Publish date is required when start date is enabled');
  }
  
  // Check scheduled_end_enabled and scheduled_end
  if (product.value.scheduled_end_enabled && (!product.value.scheduled_end?.val || !product.value.scheduled_end?.date)) {
    errors.push('End date is required when end date is enabled');
  }
  
  // Check Collections
  // if (!product.value.collections?.length) {
  //   errors.push('At least one collection must be selected');
  // }
  
  validation_errors.value = errors;
  return errors.length === 0;
};

// Real-time validation when product data changes
const updateValidationErrors = () => {
  // Skip validation for storefront
  if (props.add_type !== 'storefront') {
    validation_errors.value = [];
    return;
  }

  const errors = [];
  
  // Check Recipient
  if (!product.value.post_to.data?.length) {
    errors.push('Recipient is required');
  }
  
  // Check scheduled_start_enabled and scheduled_start
  if (product.value.scheduled_start_enabled && (!product.value.scheduled_start?.val || !product.value.scheduled_start?.date)) {
    errors.push('Publish date is required when start date is enabled');
  }
  
  // Check scheduled_end_enabled and scheduled_end
  if (product.value.scheduled_end_enabled && (!product.value.scheduled_end?.val || !product.value.scheduled_end?.date)) {
    errors.push('End date is required when end date is enabled');
  }
  
  // Check Collections
  // if (!product.value.collections?.length) {
  //   errors.push('At least one collection must be selected');
  // }
  
  validation_errors.value = errors;
};


// Methods
const handleImageError = () => {
  selectedImage.value = product.value.images?.img_400x400 || '';
};


// Check if a color is selected in multi-selection mode
const isColorSelected = (colorName) => {
  // console.log("shouldUseMultiSelection.value",shouldUseMultiSelection.value)
  // console.log("selectedVariants",selectedVariants.value)
  // console.log("colorName",selectedColor.value)
  if (shouldUseMultiSelection.value) {
    return selectedVariants.value.some(variant => variant.color === colorName);
  } else {
    return selectedColor.value === colorName;
  }
};

// Check if a size is selected in multi-selection mode
const isSizeSelected = (sizeName) => {
  if (shouldUseMultiSelection.value) {
    return selectedVariants.value.some(variant => variant.size === sizeName);
  } else {
    return selectedSize.value === sizeName;
  }
};


const addCurrentVariant = () => {
  if (canAddCurrentSelection.value) {
    const variant = {
      color: selectedColor.value,
      size: selectedSize.value,
      product_id: product.value.total_color_size_map?.find(item => 
        item.color === selectedColor.value && item.size === selectedSize.value
      )?.product_id || null,
      id: Date.now() // Unique identifier for removal
    };
    
    selectedVariants.value.push(variant);
    
    // Reset selections for next variant
    selectedColor.value = null;
    selectedSize.value = null;
    selectedImage.value = product.value.images?.img_400x400 || '';
  }
};

const removeVariant = (variantId) => {
  const index = selectedVariants.value.findIndex(v => v.id === variantId);
  if (index > -1) {
    selectedVariants.value.splice(index, 1);
  }
};

const handleProductUpdate = (updatedProduct) => {
  // console.log('Product updated:', updatedProduct);
  product.value = updatedProduct;

  // Update validation errors in real-time
  updateValidationErrors();
};

const handleProductData = (productItem) => {
  product.value = productItem;
  selectedImage.value = productItem.images?.img_400x400 || '';
  shimmerColors.value = [];


  product.value.sponsored_disclosure_name_err='';
  product.value.sponsored_disclosure_name = product.value.sponsored_disclosure_name?.replace(/\r\n/g, '\n') || 'Promoted'; 

  if (product.value.from_table) {
    is_from_tablet.value = true;
  }

  // check if unique_color_list exists and is an array
  if (product.value.unique_color_list && Array.isArray(product.value.unique_color_list) && !is_from_tablet.value) {
    product.value.unique_color_list.forEach((color) => {
      shimmerColors.value.push({
        name: color,
        image: product.value.total_color_size_map?.find((item) => item.color === color)?.thumbnail || '',
        swatch_image: product.value.total_color_size_map?.find((item) => item.color === color)?.swatch_image_url || '',
      });
    });
  } else if (product.value.unique_color_list && Array.isArray(product.value.unique_color_list) && is_from_tablet.value){
    product.value.total_color_size_map.forEach((color)=>{
      shimmerColors.value.push({
        name: color.color,
        image: color.thumbnail || '',
        swatch_image: color.swatch_image_url || '',
      });
    });
    selectedVariants.value = product.value.total_color_size_map.filter(item => product.value.unique_color_list.includes(item.color))  // 比對 color
  }

  // Automatically select a single color (if it exists and there is only one)
  if (product.value.unique_color_count === 1 && shimmerColors.value.length > 0) {
    selectColor(shimmerColors.value[0]);
  }

  // Automatically select a single size (if it exists and there is only one)
  if (product.value.unique_size_count === 1 && product.value.unique_size_list && product.value.unique_size_list.length > 0) {
    selectSize(product.value.unique_size_list[0]);
  }

  if (is_update.value) {
    const selected_variants = productItem.total_color_size_map.filter((item) => item.is_selected);

    // Check if we have multiple selections or if any variant has both color and size
    const hasMultipleSelections = selected_variants.length > 1;
    const hasCompleteVariants = selected_variants.some(item => item.size && item.color);
    // console.log("selected_variants",selected_variants)
    if (hasMultipleSelections || hasCompleteVariants ) {
      // Multi-selection mode: populate selectedVariants array
      selectedVariants.value = selected_variants.map((item, index) => ({
        color: item.color,
        size: item.size,
        id: Date.now() + index
      }));
    } else {
      // Single selection mode: set current selection
      if (selected_variants.length > 0) {
        const firstSelected = selected_variants[0];
        if (firstSelected.color) {
          selectColor(shimmerColors.value.find((item) => item.name === firstSelected.color));
        }
        if (firstSelected.size) {
          selectSize(firstSelected.size);
        }
      }
    }
  }

  // Ensure post_to has a default value if missing
  if (!product.value.post_to?.data?.length) {
    product.value.post_to = { type: 'all', data: [{ name: 'All' }] };
    product.value.click_all = true;
    product.value.status = 0;
    product.value.status_string = 'draft';
    product.value.is_draft = true;
  }

  // console.log("product.value",product.value)

  
  // Save initial state for tablet mode change detection
  if (is_from_tablet.value) {
    selectedVariants.value = [];

    if (!product.value.total_color_size_map  || product.value.total_color_size_map.length <= 1) {
      product.value.products.forEach((item, index) => {
        selectedVariants.value.push({
          product_id: item.id
        });
      });

      // console.log("selectedVariants.value",selectedVariants.value)
    } else if (product.value.total_color_size_map && Array.isArray(product.value.total_color_size_map)) {
      // Add items with is_added_in_storefront = true to selectedVariants
      const addedItems = product.value.total_color_size_map.filter(item => item.is_added_in_storefront);

      if (addedItems.length > 0) {
        const newVariants = addedItems.map((item, index) => ({
          color: item.color,
          size: item.size,
          product_id: item.product_id,
          id: Date.now() + index
        }));
        
        // Avoid duplicates by checking existing selectedVariants
        newVariants.forEach(newVariant => {
          const isDuplicate = selectedVariants.value.some(variant => 
            variant.color === newVariant.color && variant.size === newVariant.size
          );
          if (!isDuplicate) {
            selectedVariants.value.push(newVariant);
          }
        });
      }
    }

    initialSelectedVariants.value = JSON.parse(JSON.stringify(selectedVariants.value));
    
    // Save complete initial product state for change detection
    initialProductState.value = JSON.parse(JSON.stringify({
      status_string: product.value.status_string,
      post_to: product.value.post_to,
      curation_collections: product.value.curation_collections,
      permission: product.value.permission,
      scheduled_start_enabled: product.value.scheduled_start_enabled,
      scheduled_start: product.value.scheduled_start,
      scheduled_end_enabled: product.value.scheduled_end_enabled,
      scheduled_end: product.value.scheduled_end,
      notify_creators: product.value.notify_creators,
      click_all: product.value.click_all,
      is_sponsored_disclosure_enabled: product.value.is_sponsored_disclosure_enabled,
      sponsored_disclosure_name: product.value.sponsored_disclosure_name,
    }));
  }
  
  is_loading.value = false;
}

const open = (item) => {
  // console.log('Opening ProductEditPopup with item:', item);
  let open_product = item.product ? item.product : item;
  is_loading.value = true;
  // console.log(item.total_color_size_map)
  if (item.is_edit) {
    is_edit.value = true;
    is_update.value = true;
  } 
  else if (open_product.total_color_size_map && open_product.total_color_size_map.find((item) => item.is_selected)) {
    is_update.value = true;
    is_edit.value = false;
  }
  else {
    is_update.value = false;
    is_edit.value = false;
  }

  product.value = {};
  selectedImage.value = '';
  shimmerColors.value = [];
  selectedVariants.value = [];
  initialSelectedVariants.value = [];
  initialProductState.value = {};
  validation_errors.value = []; // Clear validation errors when opening
  is_tag_from_url.value = item.is_tag_from_url || false;

  // Find current product index in tagged_products array
  if (props.tagged_products && props.tagged_products.length > 0) {
    const productId = open_product.id;
    current_product_index.value = props.tagged_products.findIndex(p => p.id === productId);
    // If product not found in tagged_products, set to -1
    if (current_product_index.value === -1) {
      current_product_index.value = -1;
    }
  } else {
    current_product_index.value = -1;
  }

  handleProductData(open_product);

  visible.value = true;
};

const close = () => {
  visible.value = false;
  selectedColor.value = null;
  selectedSize.value = null;
  selectedVariants.value = [];
};

const selectColor = (color) => {
  if (shouldUseMultiSelection.value) {
    // Multi-selection logic (only for single variant type)
    const sizeCount = product.value.unique_size_count || 0;
    const existingVariantIndex = selectedVariants.value.findIndex(variant => 
      variant.color === color.name && 
      (sizeCount <= 1 || variant.size === selectedSize.value)
    );

    if (existingVariantIndex > -1) {
      // Remove existing variant
      selectedVariants.value.splice(existingVariantIndex, 1);
      // If this was the currently selected color, clear it
      if (selectedColor.value === color.name) {
        selectedColor.value = null;
        selectedImage.value = product.value.images?.img_400x400 || '';
      }
    } else {
      // Add new variant
      const newVariant = {
        color: color.name,
        size: sizeCount <= 1 ? (product.value.unique_size_list?.[0] || null) : selectedSize.value,
        product_id: product.value.total_color_size_map?.find(item => 
          item.color === color.name
        )?.product_id || null,
        id: Date.now() + Math.random() // Unique identifier
      };
      selectedVariants.value.push(newVariant);
      selectedColor.value = color.name;
      selectedImage.value = color.image;
    }
  } else if (shouldShowMultiSelectionUI.value) {
    // Color-size correlated mode with auto-selection logic
    if (selectedColor.value === color.name) {
      selectedColor.value = null;
      selectedImage.value = product.value.images?.img_400x400 || '';
      return;
    }

    selectedColor.value = color.name;
    selectedImage.value = color.image;
    
    // Auto-select size if there's only one non-disabled size available
    const availableSizesCount = availableSizes.value.filter(size => !size.disabled).length;

    if (availableSizesCount === 1) {
      const availableSize = availableSizes.value.find(size => !size.disabled);
      if (availableSize) {
        selectedSize.value = availableSize.value;
      }
    } else {

      // Clear size selection if it's no longer compatible
      if (selectedSize.value && product.value.total_color_size_map) {
        const isCompatible = product.value.total_color_size_map.some(item => 
          item.color === color.name && item.size === selectedSize.value
        );
        
        if (!isCompatible) {
          selectedSize.value = null;
        }
      }
    }
  } else {
    // Single selection logic (for correlated color-size variants)
    if (selectedColor.value === color.name) {
      selectedColor.value = null;
      selectedImage.value = product.value.images?.img_400x400 || '';
      return;
    }

    selectedColor.value = color.name;
    selectedImage.value = color.image;
    
    // Clear size selection if it's no longer compatible
    if (selectedSize.value && product.value.total_color_size_map) {
      const isCompatible = product.value.total_color_size_map.some(item => 
        item.color === color.name && item.size === selectedSize.value
      );
      if (!isCompatible) {
        selectedSize.value = null;
      }
    }
  }
};

const selectSize = (size) => {
  if (shouldUseMultiSelection.value) {

    // Multi-selection logic (only for single variant type)
    const colorCount = product.value.unique_color_count || 0;
    const existingVariantIndex = selectedVariants.value.findIndex(variant => 
      variant.size === size && 
      (colorCount <= 1 || variant.color === selectedColor.value)
    );

    if (existingVariantIndex > -1) {
      // Remove existing variant
      selectedVariants.value.splice(existingVariantIndex, 1);
      // If this was the currently selected size, clear it
      if (selectedSize.value === size) {
        selectedSize.value = null;
      }
    } else {
      // Add new variant
      const newVariant = {
        color: colorCount <= 1 ? (product.value.unique_color_list?.[0] || null) : selectedColor.value,
        size: size,
        product_id: product.value.total_color_size_map?.find(item => 
          item.size === size
        )?.product_id || null,
        id: Date.now() + Math.random() // Unique identifier
      };
      selectedVariants.value.push(newVariant);
      selectedSize.value = size;
    }
  } else if (shouldShowMultiSelectionUI.value) {
    // Color-size correlated mode with auto-selection logic
    if (selectedSize.value === size) {
      selectedSize.value = null;
      return;
    }

    selectedSize.value = size;
    
    // Auto-select color if there's only one non-disabled color available
    const availableColorsCount = availableColors.value.filter(color => !color.disabled).length;

    if (availableColorsCount === 1) {
      const availableColor = availableColors.value.find(color => !color.disabled);
      if (availableColor) {
        selectedColor.value = availableColor.name;
        selectedImage.value = availableColor.image;
      }
    } else {
      // Clear color selection if it's no longer compatible
      if (selectedColor.value && product.value.total_color_size_map) {
        const isCompatible = product.value.total_color_size_map.some(item => 
          item.color === selectedColor.value && item.size === size
        );

        if (!isCompatible) {
          selectedColor.value = null;
        }
      }
    }
  } else {
    // Single selection logic (for correlated color-size variants)
    if (selectedSize.value === size) {
      selectedSize.value = null;
      return;
    }

    selectedSize.value = size;
    
    // Clear color selection if it's no longer compatible
    if (selectedColor.value && product.value.total_color_size_map) {
      const isCompatible = product.value.total_color_size_map.some(item => 
        item.color === selectedColor.value && item.size === size
      );

      if (!isCompatible) {
        selectedColor.value = null;
      }
    }
  }
};

const isTag = (type, value) => {
  const getIsAdded = (item) => {
    if (is_from_tablet.value) {
      return false;
    }

    if (props.add_type === 'storefront') {
      return item.is_added_in_storefront;
    } else if (props.add_type === 'collection') {
      return item.is_added_in_collection;
    } else if (props.add_type === 'post') {
      return item.is_added_in_post;
    }
    return false;
  };

  // check if total_color_size_map exists and is an array
  if (!product.value.total_color_size_map || !Array.isArray(product.value.total_color_size_map)) {
    return false;
  }

  if (type === 'color') {
    const matchingItems = product.value.total_color_size_map.filter((item) => item.color === value);
    // console.log('color', matchingItems);
    return matchingItems.length > 0 && matchingItems.every((item) => getIsAdded(item));
  } else if (type === 'size') {
    const matchingItems = product.value.total_color_size_map.filter((item) => item.size === value);
    // console.log('size', matchingItems);
    return matchingItems.length > 0 && matchingItems.every((item) => getIsAdded(item));
  }

  return false;
};

const handleAdd = () => {
  // console.log('handleAdd called', canAdd.value);

  // Validate required fields first
  if (!validateRequiredFields()) {
    console.log('Validation failed:', validation_errors.value);
    // If validation fails, don't proceed and show errors
    return;
  }
  
  const currentUtcTime = moment.utc();
  const currentTimestamp = currentUtcTime.valueOf();

  // Check if scheduled start time is in the past
  if (product.value.scheduled_start_enabled && product.value.scheduled_start?.val) {
    const scheduledTime = moment.utc(product.value.scheduled_start.val);
    const scheduledTimestamp = scheduledTime.valueOf();
    
    if (scheduledTimestamp < currentTimestamp) {
      // Show confirmation dialog for past scheduled time
      const scheduledTimeStr = scheduledTime.format('MMM D, YYYY h:mm A');
      const currentTimeStr = currentUtcTime.format('MMM D, YYYY h:mm A');
      
      confirm_date.value = {
        title: 'Would you like to launch now or reschedule?',
        description: `Your scheduled launch time of  ${scheduledTimeStr} UTC has passed. ${currentTimeStr}`,
        cancel_button_text: 'No, reschedule it',
        confirm_button_text: 'Launch now',
        action: () => {
          proceedWithAdd();
        }
      };
      
      confirm_date_el.value.open();
      return;
    }
  }

  // Check if scheduled end time is in the past
  if (product.value.scheduled_end_enabled && product.value.scheduled_end?.val) {
    const scheduledEndTime = moment.utc(product.value.scheduled_end.val);
    const scheduledEndTimestamp = scheduledEndTime.valueOf();
    
    if (scheduledEndTimestamp < currentTimestamp) {
      // Show confirmation dialog for past scheduled end time
      const scheduledEndTimeStr = scheduledEndTime.format('MMM D, YYYY h:mm A');
      const currentTimeStr = currentUtcTime.format('MMM D, YYYY h:mm A');
      
      confirm_date.value = {
        title: 'Scheduled end time has passed',
        description: `Your scheduled end time of ${scheduledEndTimeStr} UTC has passed (current time: ${currentTimeStr} UTC). Do you want to proceed? The product will be disabled immediately.`,
        cancel_button_text: 'No, reschedule it',
        confirm_button_text: 'Proceed',
        action: () => {
          proceedWithAdd();
        }
      };
      
      confirm_date_el.value.open();
      return;
    }
  }

  // Proceed normally if no past schedule conflict
  proceedWithAdd();
};

const proceedWithAdd = () => {
  if (canAdd.value) {
    const variantData = {};

    // console.log('selectedVariants before add:', selectedVariants.value);
    // console.log('selectedColor:', selectedColor.value);
    // console.log('selectedSize:', selectedSize.value);
    // console.log('variantData.color:', variantData.color);
    // console.log('variantData.size:', variantData.size);
    // console.log('is_tag_from_url:', is_tag_from_url.value);
    
    if (selectedColor.value) {
      variantData.color = selectedColor.value;
    }
    
    if (selectedSize.value) {
      variantData.size = selectedSize.value;
    }


    
    if (product.value.total_color_size_map && Array.isArray(product.value.total_color_size_map)) product.value.total_color_size_map.forEach((item) => {
      // Check if this item matches any of the selected variants
      item.is_selected = selectedVariants.value.some(variant => {
        const colorMatch = variant.color ? item.color === variant.color : true;
        const sizeMatch = variant.size ? item.size === variant.size : true;
        return colorMatch && sizeMatch;
      });
    });

    // console.log('variantData to add:', variantData, is_tag_from_url.value);

    if (is_tag_from_url.value) {
      // console.log('variantData', variantData);
      // console.log('is_tag_from_url', selectedVariants.value);

      product.value.is_tag_from_url = true;
      event.emit('selected-variant-from-url', product.value);
    } 

    else if (shouldShowMultiSelectionUI.value) {
      // console.log('selectedVariants in multi-selection UI:', product.value);

      // Color-size correlated mode with multi-selection UI
      event.emit('selected-variant', 
        {
          product: product.value,
          id: product.value.id,
          is_update: is_update.value,
          selectedVariants: {
            id: product.value.id,
            variant: variantData,
            isMultiple: selectedVariants.value.length > 0,
            variants: selectedVariants.value.length > 0 ? selectedVariants.value : [variantData]
          }
        }
      );
    }
    
    else {
      console.log('selectedVariants in single-selection mode:', product.value, is_update.value);


      event.emit('selected-variant', 
        {
          product: product.value,
          id: product.value.id,
          is_update: is_update.value,
          selectedVariants: {
            id: product.value.id,
            variant: selectedVariants.value[0],
            isMultiple: selectedVariants.value.length > 1,
            variants: selectedVariants.value
          }
        }
      );
    }

    if (is_update.value) {
      // sync product data when in update mode
      const updatedProduct = props.tagged_products.find(p => p.id === product.value.id);
      if (updatedProduct) {
        updatedProduct.post_to = product.value.post_to;
        updatedProduct.status = product.value.status;
        updatedProduct.permission = product.value.permission;
        updatedProduct.curation_collections = product.value.curation_collections;
        updatedProduct.scheduled_end = product.value.scheduled_end;
        updatedProduct.scheduled_start = product.value.scheduled_start;
        updatedProduct.scheduled_start_enabled = product.value.scheduled_start_enabled;
        updatedProduct.scheduled_end_enabled = product.value.scheduled_end_enabled;
        updatedProduct.notify_creators = product.value.notify_creators;
        updatedProduct.status_string = product.value.status_string;
        updatedProduct.is_sponsored_disclosure_enabled = product.value.is_sponsored_disclosure_enabled;
        updatedProduct.sponsored_disclosure_name = product.value.sponsored_disclosure_name;
      }
        
    }

    close();
  }
};

const showDateTime = computed(() => {
  if (product.value.scheduled_start_enabled && product.value.scheduled_start?.date && product.value.scheduled_start?.time) {
    return `${product.value.scheduled_start?.date} ${product.value.scheduled_start?.time}`
  }
  return 'now';
});

const handleUpdate = () => {
  if(!product.value.sponsored_disclosure_name){
    product.value.sponsored_disclosure_name_err = 'Cannot leave blank.';
    return;
  }
  
  // Check if status is changing from draft to active or scheduled
  const initialStatus = initialProductState.value.status_string;
  const currentStatus = product.value.status_string;
  
  if (initialStatus === 'draft' && (currentStatus === 'active' || currentStatus === 'scheduled')) {
    // Show confirmation popup
    // confirm_data.value = {
    //   title: currentStatus === 'active' ? 'Publish this product?' : 'Publish this product immediately?',
    //   description: currentStatus === 'active' ? 
    //     `This will display the product on all selected creators storefronts immediately.` : 
    //     `This action will override the scheduled date to publish at ${showDateTime.value} and will display the product on all selected creators storefronts immediately.`,
    //   action: () => {
    //     performUpdate();
    //   }
    // };
        confirm_data.value = {
          title:'Publish this product?',
          description: 
            `This will display the product on all selected creators storefronts ${showDateTime.value}.` ,
          action: () => {
            performUpdate();
          }
      };
    confirm_dialog_el.value.open();
    return;
  }
  
  // If no confirmation needed, proceed directly
  performUpdate();
};

const performUpdate = () => {
  if(is_loading.value){
    return;
  }
  is_loading.value = true;
  let data = product.value;
  let creator_ids = [];
  let tag_ids = [];
  let collection_ids = [];
  
  // Only process creator_ids and tag_ids if not applying to all creators
  if (!data.click_all) {
    // console.log('data.post_to', data.post_to);
    data.post_to.data.forEach((item) => {
      if (item.motom_user_id) {
        creator_ids.push(item.recipient_id);
      } else {
        tag_ids.push(item.id);
      }
    });
  }
  
  // Process collections
  if (data.curation_collections && data.curation_collections.length > 0) {
    data.curation_collections.forEach((item) => {
      collection_ids.push(item.id);
    });
  }

  const perm = Array.isArray(data.permission)
    ? data.permission
    : JSON.parse(data.permission || '[]');
    
  let product_ids = [];

  selectedVariants.value.forEach((variant) => {
    // console.log('variant', variant)
    product_ids.push(variant.product_id);
    // const matchedItem = product.value.total_color_size_map.find(item => 
    //   item.color === variant.color && item.size === variant.size
    // );
    // if (matchedItem) {
    //   product_ids.push(matchedItem.product_id);
    // }
  });

  // console.log(product_ids);
  // return;

  let row = {
    curation_product_id: data.id,
    product_ids: product_ids,
    is_feature: perm?.indexOf('allow_feature') !== -1 ? 1 : 0,
    is_schedule: data.status_string === 'scheduled' ? 1 : 0,
    allow_creator_remove: perm?.indexOf('allow_remove') !== -1 ? 1 : 0,
    notify_creator_on_publish: data.notify_creators ? 1 : 0,
    apply_to_all_creators: data.click_all ? 1 : 0,
    status: data.status_string === 'scheduled' ? 'active' : data.status_string,
    anchor_creator_ids: creator_ids,
    tag_ids: tag_ids,
    curation_collection_ids: collection_ids,
    is_sponsored_disclosure_enabled: data.is_sponsored_disclosure_enabled ? 1 : 0,
    sponsored_disclosure_name: data.sponsored_disclosure_name || 'Promoted'
  };

  // Only add datetime fields if they exist and are enabled (following Index.vue pattern)
  if (data.scheduled_start_enabled && data.scheduled_start.val) {
    row.publish_datetime = data.scheduled_start.val;
  }

  if (data.scheduled_end_enabled && data.scheduled_end.val) {
    row.disable_datetime = data.scheduled_end.val;
  }

  // console.log("Update payload:", row);
  
  ajax
    .post('/storefront-curation/update-curation-product', JSON.stringify([row]), {
      headers: {
        'Content-Type': 'application/json'
      }
    })
    .then(async(res) => {
      is_loading.value = false;
      openNotBottom('Product updated successfully', 'success');
      await PublishProduct(row);
      // Refresh the product table in storefront
      event.emit('refresh-storefront-product-table');
      close();
    })
    .catch((err) => {
      console.log('err',err)
      is_loading.value = false;
    });
};

const PublishProduct = async (row) => {
  if(row.status!=='active' && row.status!=='scheduled'){
    return;
  }
  try {
      const params_data = {
        'curation_product_ids[]': [row.curation_product_id],
      };
      await ajax.post('/storefront-curation/publish-curation-product',  params_data , {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

  } catch (error) {
    console.error('Publish product failed:', error);
    openNotBottom('Publish failed', 'error');
  } 
};

// Navigate to next product in tagged_products array
const next = () => {
  if (canNext.value) {
    current_product_index.value++;
    const nextProduct = props.tagged_products[current_product_index.value];
    if (nextProduct) {
      // Update the current product data
      handleProductData(nextProduct);
    }
  }
};

// Navigate to previous product in tagged_products array  
const prev = () => {
  if (canPrev.value) {
    current_product_index.value--;
    const prevProduct = props.tagged_products[current_product_index.value];
    if (prevProduct) {
      // Update the current product data
      handleProductData(prevProduct);
    }
  }
};

// Remove current product
const remove = () => {
  emit('remove-product', product.value);
};

const deleteProduct=() => {
  event.emit('open-storefront-product-handle-dialog', {
    type: 'Delete',
    data: [product.value],
    callback: () => {
      close();
    }
  });
};

onMounted(() => {
  if (!props.is_edit_mode) {
    event.off('select-variant');
    event.on('select-variant', open);
  }  
});

// Watch for changes in product schedule settings to update validation
watch(
  () => [
    product.value.scheduled_start_enabled,
    product.value.scheduled_start,
    product.value.scheduled_end_enabled,
    product.value.scheduled_end,
    product.value.post_to?.data
  ],
  () => {
    updateValidationErrors();
  },
  { deep: true }
);

defineExpose({
  open,
  close
});

</script>

<style lang="scss">
.select-product-variants {
  overflow: hidden;
  max-height: 100%;
  margin: 0 auto;

  .disabled-box {
    position: absolute;
    width: 100%;
    height: 100%;
    z-index: 1;
  }

  .el-dialog__header {
    display: none;
  }

  .el-dialog__body {
    display: flex;
    flex-direction: column;
  }

  &__header {
    display: flex;
    align-items: center;

    h3 {
      margin: 0;
      font: var(--medium-24);
      color: var(--Text-primary);
      flex: 1;
      display: flex;
      align-items: center;
    }

    .el-button {
      padding: 0;
      color: var(--Text-secondary);
      min-width: 32px;
      margin-right: 4px;

      &:hover {
        color: var(--Text-primary);
      }

      svg {
        width: 16px;
        height: 16px;
      }
    }

    .actions {
      display: flex;
      align-items: center;
      gap: 8px;

      .el-button {
        margin: 0;

        svg {
          width: 12px;
          height: 12px;
        }

        &.delete-btn {
          color: var(--Red-30);
          

          svg {
            width: 16px;
            height: 16px;
          }
        }
      }
    }
  }

  &__body {
    display: flex;
    padding: 24px 0;
    gap: 24px;
    flex: 1;
    overflow-y: auto;
    max-height: calc(100vh - 260px);

    // Grid layout for storefront
    &.storefront-layout {
      display: grid;
      grid-template-columns: 420px 1fr;
      grid-template-rows: auto 1fr;
      grid-template-areas: 
        "product-info right-panel"
        "variant-selections right-panel";
      gap: 24px;
    }
  }

  &__footer {
    margin-top: auto;
    position: relative;

    &::before {
      content: '';
      position: absolute;
      left: 0;
      top: -16px;
      width: 100%;
      height: 16px;
      background: linear-gradient(to bottom, rgba(255, 255, 255, 0), rgba(255, 255, 255, 1));
      z-index: 2;
    }

    .validation-errors {
      padding: 12px 0;

      .error-message {
        color: var(--Red-30);
        font-size: 14px;
        background-color: var(--Red-05);
        border: 1px solid var(--Red-20);
        border-radius: 6px;
        margin-bottom: 8px;
        
        &:last-child {
          margin-bottom: 0;
        }
      }
    }

    .btns {
      display: flex;
      align-items: center;
      gap: 12px;

      .el-button {
        height: 40px;
        margin: 0;
      }

      &:has(.el-button:only-child) .el-button {
        flex: 1;
      }

      &:has(.el-button:not(:only-child)) .el-button {
        flex: 1;
      }
    }


  }

  .product-info {
    flex: 0 0 420px;
    // position: sticky;
    // top: 0;
    background-color: var(--Black-00);
    // z-index: 5;

    &::after {
      content: '';
      display: block;
      height: 48px;
    }
    
    // Grid layout positioning
    .storefront-layout & {
      grid-area: product-info;
      flex: none;
    }
    
    .product-image {
      width: 100%;
      min-height: 320px;
      border-radius: var(--radius-medium);
      margin: 16px 0;
      position: relative;

      .el-image {
        position: absolute;
        left: 0;
        top: 0;
        height: 100%;
        width: 100%;
        display: flex;
        align-items: center;
        justify-content: center;
      }

      img {
        max-width: 100%;
        max-height: 100%;
        object-fit: contain;
      }

      @media screen and (max-width: $mobile_size) {
        margin-top: 0;
        max-width: 320px;
        margin-left: auto;
        margin-right: auto;
        min-height: unset;

        &::before {
          content: '';
          display: block;
          padding-top: 100%;
        }
      }
    }

    .product-details {
      padding-bottom: 8px;
      
      .brand-name {
        color: var(--Text-secondary);
        margin-bottom: 4px;
      }

      .product-name {
        font: var(--medium-20);
        color: var(--Text-primary);
      }
    }
  }

  .variant-selections {
    flex: 1;
  }

  .variant-selections-wrapper {
    flex: 1;
    
    &::after {
      content: '';
      display: block;
      height: 48px;
    }

    // Grid layout positioning
    .storefront-layout & {
      grid-area: variant-selections;
      flex: none;
    }
  }

  // ProductEditRightPanel positioning in grid
  .storefront-layout .product-edit-right-panel {
    grid-area: right-panel;
  }

  .selected-variants {
    margin: 24px 0;


    .selected-variants-header {
      margin-bottom: 16px;
      padding: 0 0 12px;
      border-bottom: 1px solid var(--Black-10);

      h4 {
        margin: 0;
        font: var(--medium-16);
        color: var(--Text-primary);
      }
    }

    .selected-variants-list {
      display: flex;
      flex-direction: column;
      gap: 8px;
    }

    .selected-variant-item {
      padding: 6px 6px 6px 10px;
      border-radius: var(--radius);
      background-color: var(--Black-10);
      position: relative;

      .variant-info {
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
        max-width: calc(100% - 32px);
      }

      .remove-btn {
        padding: 4px;
        color: var(--Text-primary);
        min-width: auto;
        position: absolute;
        right: 4px;
        top: 50%;
        transform: translateY(-50%);

        svg {
          width: 12px;
          height: 12px;
        }
      }
    }
  }

  .variant-section {
    margin-bottom: 8px;

    &:last-child {
      margin-bottom: 0;
    }
  }

  .variant-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 0 0 10px;
    margin-bottom: 12px;
    border-bottom: 1px solid var(--Black-20);

    h4 {
      margin: 0;
      font: var(--medium-16);
      color: var(--Text-primary);
    }

    .variant-count {
      color: var(--Text-default);
      font-size: 12px;
      margin-right: 8px;
    }
  }

  .selected-info {
    padding: 0 0 12px;
    font: var(--medium-12);
    color: var(--Text-primary);
  }

  .finish-group {
    margin-bottom: 24px;
    padding: 0 0 16px;

    &:last-child {
      margin-bottom: 0;
    }

    h5 {
      margin: 0 0 12px 0;
      font: var(--medium-14);
      color: var(--Text-primary);
    }
  }

  .color-options {
    display: flex;
    gap: 8px;
    flex-wrap: wrap;
  }

  .color-option {
    width: 64px;
    height: 64px;
    border: 1px solid var(--Black-20);
    background-color: var(--Black-10);
    border-radius: var(--radius);
    overflow: hidden;
    cursor: pointer;
    transition: all 0.2s;
    position: relative;

    &:hover {
      border-color: var(--Black-50);
    }

    &.active {
      border-color: var(--Black-100);

      &::after {
        content: '';
        position: absolute;
        right: 0;
        bottom: 0;
        width: 100%;
        height: 100%;
        background-color: rgba(0, 0, 0, 0.5);
      }

      &::before {
        height: 20px;
        width: 20px;
        position: absolute;
        left: 50%;
        top: 50%;
        transform: translate(-50%, -50%);
        content: '';
        background-color: var(--Green-30);
        z-index: 1;
        border-radius: 50%;
      }

      svg {
        position: absolute;
        left: 50%;
        top: 50%;
        transform: translate(-50%, -50%);
        width: 22px;
        height: 22px;
        fill: var(--Black-00);
        z-index: 2;
      }
    }

    .el-image,
    img {
      width: 100%;
      height: 100%;
      object-fit: cover;
      left: 0;
      top: 0;
      position: absolute;
    }


    &.is-added {
      pointer-events: none;
      border-width: 0px;


      &::before {
        position: absolute;
        left: 0;
        top: 0;
        height: 100%;
        width: 100%;
        z-index: 2;
        content: '';
        background-color: rgba(0,0,0,.7);
      }

      &::after {
        content: 'Added';
        position: absolute;
        left: 0%;
        top: 0%;
        z-index: 3;
        font: var(--medium-16);
        color: var(--Black-00);
        text-align: center;
        height: 100%;
        width: 100%;
        display: flex;
        align-items: center;
        justify-content: center;
      }
    }

    &.is-disabled {
      pointer-events: none;
      
      &::before {
        content: '';
        position: absolute;
        left: 0;
        top: 0;
        width: 100%;
        height: 100%;
        background-color: rgba(0,0,0,.5);
        z-index: 2;
      }

      svg {
        position: absolute;
        left: 50%;
        top: 50%;
        transform: translate(-50%, -50%);
        width: 32px;
        height: 32px;
        fill: var(--Black-00);
        opacity: .8;
        z-index: 3;
      }
    }
  }

  .size-options {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }

  .size-option {
    padding: 8px 12px;
    border: 1px solid var(--Black-20);
    border-radius: var(--radius);
    cursor: pointer;
    transition: all 0.2s;
    color: var(--Text-primary);
    font: var(--medium-16);
    position: relative;

    &:hover {
      border-color: var(--Black-50);
    }

    &.active {
      border-color:  var(--Black-100);
      color: var(--Black-100);

      svg {
        position: absolute;
        right: 8px;
        top: 50%;
        transform: translateY(-50%);
        width: 16px;
        height: 16px;
      }
    }

    &.is-added {
      border-color: var(--Black-20);
      background-color: var(--Black-10);
      color: var(--Black-50);
      pointer-events: none;

      &::after {
        content: 'Added';
        position: absolute;
        right: 12px;
        color: var(--Black-100);
      }
    }

    &.is-disabled {
      pointer-events: none;
      opacity: 0.6;
      background-color: var(--Black-05);
      color: var(--Black-60);
      display: flex;
      align-items: center;

      &::after {
        content: 'Unavailable';
        flex: 1;
        text-align: right;
        font-size: 14px;
      }
    }
  }

  .mobile-variant-selector {
    display: none;
  }

  .select-product-variants__body {
    position: relative;
  }

  .dis-input-box{
    padding-left: 30px;
    margin-top: 4px;

    input{
      font-size: 12px;
    }
  }

  .err-input .el-input__wrapper{
    box-shadow: 0 0 0 1px var(--red, var(--red)) inset;;
  }

  // @media screen and (max-width: $mobile_size) {
  //   max-height: 100%;
  //   max-width: 100%;
  //   height: 100%;
  //   width: 100%;
  //   border-radius: 0;
  //   padding-top: 8px;
  //   padding-left: 0;
  //   padding-right: 0;
  //   overflow: hidden;


  //   .product-info {
  //     overflow: auto;
  //     padding: 0 16px;
  //     position: sticky;
  //     top: 0;
  //   }

  //   .variant-selections {
  //     display: none;
  //   }

  //   .el-dialog__body {
  //     padding: 0;
  //     display: grid;
  //     grid-template-rows: auto 1fr auto;
  //     height: 100%;
  //     position: relative;
  //     max-height: 100%;
  //     gap: 0;
  //   }

  //   .select-product-variants__body {
  //     display: grid;
  //     grid-template-rows: auto 1fr;
  //     padding: 24px 0 0;
  //     overflow: auto;
  //     gap: 0;
  //   }

  //   .select-product-variants__footer {
  //     padding-left: 16px;
  //     padding-right: 16px;
  //     padding-top: 16px;
  //     border-radius: 6px 6px 0 0;
  //     position: sticky;
  //     bottom: 0;
  //     background-color: var(--Black-00);
  //     z-index: 10;
  //     box-shadow: 0px -4px 8px 0px rgba(0, 0, 0, 0.10);

  //     .validation-errors {
  //       margin-bottom: 8px;
        
  //       .error-message {
  //         font-size: 13px;
  //         padding: 6px 10px;
  //       }
  //     }
  //   }

  //   // .mobile-variant-selectors {
  //   //   display: flex;
  //   //   flex-direction: column;
  //   //   margin-bottom: 8px;

  //   //   .selected-info {
  //   //     padding: 8px 0 0;
  //   //   }
  //   // }

  //   // .mobile-variant-selector {
  //   //   display: block;
  //   //   // padding-bottom: 12px;
  //   //   // border-bottom: 1px solid var(--Black-20);

  //   //   .variant-label {
  //   //     display: flex;
  //   //     align-items: center;
  //   //     font: var(--medium-14);
  //   //     color: var(--Text-primary);
  //   //     cursor: pointer;
  //   //     padding: 4px 0 8px 0;
  //   //     border-bottom: 1px solid var(--Black-20);
        

  //   //     .variant-count {
  //   //       color: var(--Text-secondary);
  //   //       font-size: 12px;
  //   //       margin-left: 4px;
  //   //       display: flex;
  //   //       justify-content: flex-end;
  //   //       align-items: center;
  //   //       gap: 6px;
  //   //       flex: 1;

  //   //       svg {
  //   //         height: 12px;
  //   //         width: 12px;

  //   //         &.active {
  //   //           transform: rotate(-180deg);
  //   //         }
  //   //       }
  //   //     }
  //   //   }

  //   //   .variant-select {
  //   //     width: 100%;
  //   //     margin: 8px 0;

  //   //     .el-input__inner {
  //   //       height: 44px;
  //   //       font: var(--regular-16);
  //   //       border-radius: var(--radius);
  //   //       border: 1px solid var(--Black-20);
  //   //     }
  //   //   }
  //   // }



  //   .btns {
  //     .el-button {
  //       height: 44px;
  //       border-radius: var(--radius);
  //       margin: 0;
  //     }

  //     &:has(.el-button:only-child) .el-button {
  //       width: 100%;
  //     }

  //     &:has(.el-button:not(:only-child)) {
  //       display: flex;
  //       gap: 12px;
        
  //       .el-button {
  //         flex: 1;
  //       }
  //     }
  //   }
  // }
}

/* Mobile variant selector dropdown styles */
.variant-select-popper {
  .el-select-dropdown__list {
    padding: 12px 0;
    overflow: hidden;
  }

  .el-select-dropdown__item {
    padding: 4px 16px;
    box-sizing: content-box;


    i {
      height: 14px;
      width: 14px;
      display: block;

      svg {
        height: 100%;
        width: 100%;
        fill: var(--Green-30);
      }
    }
  }

  .color-option-item {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 0;
    margin: 0;

    .color-preview {
      width: 32px;
      height: 32px;
      border-radius: var(--radius-small);
      overflow: hidden;
      border: 1px solid var(--Black-20);
      flex-shrink: 0;

      .el-image,
      img {
        width: 100%;
        height: 100%;
        object-fit: cover;
        display: block;
      }
    }

    .color-name {
      flex: 1;
      color: var(--Text-primary);
    }
  }

  .size-option-item {
    display: flex;
    align-items: center;
    justify-content: space-between;

    .size-name {
      color: var(--Text-primary);
    }
  }

  .is-added {
    pointer-events: none;

    & > div {
      .el-image {
        opacity: .4;
      }

      .size-name,
      .color-name {
        color: var(--Black-80);
      }

      .added-tag {
        font: var(--medium-12);
        color: var(--Text-primary);
      }
    }
  }

  .el-select-dropdown__item.is-added {
    color: var(--Text-secondary);
    background-color: var(--Black-05);
  }

  .el-select-dropdown__item.is-disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
}
</style>
